/**
 * Demonstration clips for a lesson — short excerpts of the moments where
 * music was played rather than spoken.
 *
 * ONE component, two call sites (instructor `LessonSummary` and portal
 * `StudentLessonView`), deliberately: they render the same thing from the
 * same shape, and two copies would drift the way the PDF export did. The only
 * difference between them is which axios client and path they pass in.
 *
 * Renders NOTHING when a lesson has no clips, which is the common case — most
 * lessons have none (the feature is off by default, and a consent granted
 * before clips were disclosed never produces one). An empty section header
 * promising audio that isn't there is worse than silence.
 *
 * Clip URLs are short-lived signed links minted per request, so this fetches
 * on mount rather than reading anything off the lesson payload.
 *
 * `audience` picks the heading wording from the shared label table. It reads
 * the same to both today; the parameter exists so this component cannot be
 * the one place a future audience split is missed.
 */
import { useEffect, useState } from "react";
import { Card, Icon } from "@/components/studio/primitives";
import { sectionLabel } from "@/lib/lessonSections";

/** Seconds → "0:07". Clips are always well under a minute. */
const formatClipLength = (seconds) => {
  const total = Math.max(0, Math.round(seconds || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

const LessonClips = ({ client, path, audience = "family" }) => {
  const [clips, setClips] = useState([]);

  useEffect(() => {
    if (!client || !path) return undefined;
    const controller = new AbortController();
    client
      .get(path, { signal: controller.signal })
      .then((res) => setClips(res.data?.clips || []))
      // A lesson with no clips, a disabled feature, and an expired link all
      // mean the same thing here: show nothing.
      .catch(() => {});
    return () => controller.abort();
  }, [client, path]);

  if (!clips.length) return null;

  return (
    <Card className="mb-4">
      <div className="mb-3.5 flex items-center gap-2">
        <Icon name="music" size={15} strokeWidth={1.8} className="text-[var(--accent-yellow-text)]" />
        <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]">
          {sectionLabel("clips", audience)}
        </span>
      </div>
      <p className="mb-3.5 font-sans text-[13px] leading-relaxed text-[var(--text-tertiary)]">
        Short excerpts from where music was played in this lesson. They are deleted automatically after 30 days.
      </p>
      <div className="flex flex-col gap-3.5">
        {clips.map((clip) => (
          <div key={clip.clip_id} className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-sans text-[13.5px] leading-snug text-[var(--text-secondary)]">
                {/* Falls back to a NEUTRAL label when the pipeline could not
                    establish who was speaking before the passage — asserting
                    "Teacher demonstration" on a guess is how a mislabeled
                    transcript becomes a mislabeled recording. */}
                {clip.label || (clip.speaker ? `${clip.speaker} demonstration` : "Demonstration")}
              </span>
              <span className="shrink-0 font-mono text-[11px] text-[var(--text-tertiary)]">
                {formatClipLength(clip.duration_seconds)}
              </span>
            </div>
            {/* Native controls: playback here is scrub-and-replay, which the
                browser already does better than anything hand-rolled. */}
            <audio controls preload="none" src={clip.url} className="w-full" data-testid="lesson-clip-audio">
              Your browser cannot play this clip.
            </audio>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default LessonClips;
